import { apiBase, ensureApiSession } from "@/components/toLoad/api";

const watches = new Map();
let socket = null;
let companyId = null;
let reconnectTimer = null;
let pingTimer = null;
let ready = false;
let attempt = 0;
let connectPromise = null;

function isDocPath(path) {
  if (path === "me" || path === "company") return true;
  return path.split("/").filter(Boolean).length % 2 === 0;
}

function toPlain(value, seen = new WeakSet()) {
  if (value == null) return value;
  const type = typeof value;
  if (type !== "object") return type === "function" ? undefined : value;
  if (seen.has(value)) return undefined;
  seen.add(value);
  if (Array.isArray(value)) return value.map((item) => toPlain(item, seen));
  const out = {};
  for (const key of Object.keys(value)) {
    if (key === "__ob__" || key === "__v_skip") continue;
    out[key] = toPlain(value[key], seen);
  }
  return out;
}

function emit(path, meta = {}) {
  const bucket = watches.get(path);
  if (!bucket) return;
  const docs = [...bucket.docs.values()];
  const payload = isDocPath(bucket.path)
    ? {
      exists: docs.length > 0,
      id: docs[0]?.id,
      data: () => toPlain(docs[0]) || {},
    }
    : {
      empty: docs.length === 0,
      size: docs.length,
      docs: docs.map((doc) => ({
        id: doc.id,
        data: () => toPlain(doc) || {},
      })),
    };
  for (const cb of bucket.callbacks) cb(payload, meta);
}

function send(message) {
  if (socket?.readyState !== WebSocket.OPEN || !ready) return;
  socket.send(JSON.stringify(message));
}

function resubscribe() {
  for (const [key, bucket] of watches) send({ type: "watch", path: bucket.path, companyId: bucket.companyId || key.split(":")[0] });
}

function scheduleReconnect() {
  clearTimeout(reconnectTimer);
  const delay = Math.min(30000, 1000 * 2 ** attempt);
  attempt += 1;
  reconnectTimer = setTimeout(connect, delay);
}

async function connect() {
  if (!companyId) return;
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;
  if (connectPromise) return connectPromise;

  connectPromise = openSocket().finally(() => {
    connectPromise = null;
  });
  return connectPromise;
}

async function openSocket() {
  const token = await ensureApiSession();
  if (!companyId) return;
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;

  const base = apiBase().replace(/^http/, "ws");
  const ws = new WebSocket(`${base}/live`);
  socket = ws;
  ready = false;

  ws.addEventListener("open", () => {
    if (socket !== ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "auth", token }));
  });

  ws.addEventListener("message", (event) => {
    if (socket !== ws) return;
    const message = JSON.parse(event.data);
    if (message.type === "ready") {
      ready = true;
      attempt = 0;
      resubscribe();
      clearInterval(pingTimer);
      pingTimer = setInterval(() => send({ type: "ping" }), 30000);
      return;
    }
    if (message.type === "snapshot") {
      const bucket = watches.get(`${message.companyId || ""}:${message.path}`);
      if (!bucket) return;
      bucket.docs = new Map((message.docs || []).map((doc) => [doc.id, doc]));
      emit(`${message.companyId || ""}:${message.path}`, { type: "snapshot" });
      return;
    }
    if (message.type === "change") {
      const bucket = watches.get(`${message.companyId || ""}:${message.path}`);
      if (!bucket) return;
      if (message.op === "removed") bucket.docs.delete(message.id);
      else if (message.data) bucket.docs.set(message.id, message.data);
      emit(`${message.companyId || ""}:${message.path}`, {
        type: "change",
        op: message.op,
        id: message.id,
        data: message.data,
      });
    }
  });

  ws.addEventListener("close", () => {
    if (socket !== ws) return;
    ready = false;
    clearInterval(pingTimer);
    scheduleReconnect();
  });
}

export function watch(nextCompanyId, path, cb) {
  companyId = nextCompanyId || companyId;
  const key = `${nextCompanyId || ""}:${path}`;
  let bucket = watches.get(key);
  if (!bucket) {
    bucket = { callbacks: new Set(), docs: new Map(), path, companyId: nextCompanyId || "" };
    watches.set(key, bucket);
  }
  bucket.callbacks.add(cb);
  connect();
  if (ready) send({ type: "watch", path, companyId: nextCompanyId });
  if (bucket.docs.size) emit(key, { type: "snapshot" });

  return () => {
    const current = watches.get(key);
    if (!current) return;
    current.callbacks.delete(cb);
    if (current.callbacks.size === 0) {
      watches.delete(key);
      send({ type: "unwatch", path, companyId: nextCompanyId });
    }
  };
}

export async function saveDoc(path, id, data, method) {
  const { apiUser } = await import("@/components/toLoad/api");
  const url = id ? `${path}/${id}` : path;
  return apiUser({
    method: method || (id ? "patch" : "post"),
    url,
    data,
  });
}
